import React, { useEffect, useState } from 'react';

import { FormControl, FormHelperText, Grid, IconButton, InputLabelProps, TextField } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import {
  BaseSingleInputFieldProps,
  CalendarIcon,
  ClearIcon,
  DatePicker,
  DateValidationError,
  FieldSection,
  UseDateFieldProps,
} from '@mui/x-date-pickers';
import ConditionalParent from '../ConditionalParent';

const DAYJS_FORMATS = ['YYYYMMDD', 'YYYY-MM-DD'];

interface InputFieldProps
  extends UseDateFieldProps<Dayjs>,
    BaseSingleInputFieldProps<Dayjs | null, Dayjs, FieldSection, DateValidationError> {
  onChange: (date: Dayjs | null) => void;
  onOpen: () => void;
  error?: boolean;
  onlyValid?: boolean;
  disableClear?: boolean;
  required?: boolean;
  InputLabelProps?: InputLabelProps;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  sx?: object;
}

function InputField({
  label,
  id,
  disabled,
  required,
  value,
  onChange,
  onOpen,
  InputProps: { ref } = {},
  inputProps: { 'aria-label': ariaLabel } = {},
  onlyValid = true,
  error,
  disableClear,
  className,
  sx,
  InputLabelProps,
  minDate,
  maxDate,
}: InputFieldProps) {
  const [input, setInput] = useState(value?.isValid() ? value.format('YYYY-MM-DD') : '');

  useEffect(() => {
    if (!value) return setInput('');
    const curr = dayjs(input, DAYJS_FORMATS);
    if (value.isValid() && !value.isSame(curr, 'day')) setInput(value.format('YYYY-MM-DD') ?? '');
  }, [value]);

  return (
    <TextField
      sx={sx}
      ref={ref}
      id={id}
      aria-label={ariaLabel}
      label={label}
      placeholder="YYYY-MM-DD"
      value={input}
      error={error}
      onChange={({ target }) => {
        setInput(target.value);
        if (!target.value && !disableClear) return onChange(null);
        const day = dayjs(target.value, DAYJS_FORMATS);
        if (!onlyValid || day.isValid()) onChange(day);
      }}
      onBlur={() => {
        let day: Dayjs | null = dayjs(input, DAYJS_FORMATS);
        if (!day.isValid() && disableClear) day = value!;

        setInput(day?.isValid() ? day.format('YYYY-MM-DD') : onlyValid ? '' : input);
      }}
      disabled={disabled}
      required={required}
      InputProps={{
        startAdornment: (
          <IconButton onClick={onOpen}>
            <CalendarIcon />
          </IconButton>
        ),
        endAdornment: !disabled && !disableClear && (
          <IconButton onClick={() => onChange(null)}>
            <ClearIcon />
          </IconButton>
        ),
      }}
      InputLabelProps={InputLabelProps}
      className={className}
    />
  );
}

type Props = {
  size?: boolean | 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | null;
  label: string;
  error?: string | null;
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  onlyValid?: boolean;
  disabled?: boolean;
  required?: boolean;
  disableClear?: boolean;
  InputLabelProps?: InputLabelProps;
  className?: string;
  sx?: object;
};

const FormFreeDateField: React.FC<Props> = ({
  size,
  label,
  error,
  value,
  onChange,
  minDate,
  maxDate,
  onlyValid,
  required,
  disabled,
  disableClear,
  InputLabelProps,
  className,
  sx,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <ConditionalParent
      on={size !== null}
      parent={(children) => (
        <Grid item xs={size!}>
          <FormControl variant="standard" margin="normal" required={required} fullWidth>
            {children}
          </FormControl>
        </Grid>
      )}
    >
      <>
        <DatePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          slots={{ field: InputField as any }}
          slotProps={{
            field: {
              onChange,
              onOpen: () => setOpen(true),
              onlyValid,
              error: !!error,
              InputLabelProps,
              disableClear,
              className,
              sx,
              required,
            } as any,
          }}
          onChange={onChange}
          label={label}
          views={['year', 'month', 'day']}
          value={value}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
        />
        {error !== undefined && <FormHelperText error>{error || ' '}</FormHelperText>}
      </>
    </ConditionalParent>
  );
};

export default FormFreeDateField;
